import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Currency = "USD" | "INR";

type Ctx = {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  toggle: () => void;
};

const CurrencyContext = createContext<Ctx | null>(null);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState<Currency>("INR");

  useEffect(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("mgcl-currency") : null;
    if (stored === "USD" || stored === "INR") setCurrency(stored);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("mgcl-currency", currency);
  }, [currency]);

  return (
    <CurrencyContext.Provider
      value={{ currency, setCurrency, toggle: () => setCurrency(currency === "USD" ? "INR" : "USD") }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) return { currency: "USD" as Currency, setCurrency: () => {}, toggle: () => {} };
  return ctx;
}

export function CurrencyToggle({ tone = "light" }: { tone?: "light" | "dark" }) {
  const { currency, setCurrency } = useCurrency();
  const base =
    tone === "dark"
      ? "border-white/20 bg-white/5 text-white/70"
      : "border-brand-deep/15 bg-white text-brand-deep/60";
  const active =
    tone === "dark" ? "bg-brand-accent text-white" : "bg-brand-deep text-white";
  return (
    <div className={`inline-flex items-center rounded-full border p-1 ${base}`}>
      {(["USD", "INR"] as Currency[]).map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => setCurrency(c)}
          className={`rounded-full px-4 py-1.5 text-xs font-bold tracking-widest transition-all ${
            currency === c ? active : "hover:opacity-100"
          }`}
          aria-pressed={currency === c}
        >
          {c}
        </button>
      ))}
    </div>
  );
}